import { cn } from '@zafroi/ui-kit';
import { ArrowUp, X } from 'lucide-react';

interface NewSincePillProps {
  /**
   * Items added since the page was opened. The pill renders only when this
   * is > 0; consumers can pass through without gating.
   */
  count: number;
  /**
   * Snaps the anchor forward — clears `count` and any row highlighting.
   * The pill calls this on its primary click target and on Dismiss.
   */
  onAcknowledge: () => void;
  /**
   * Singular/plural noun. Defaults to "item" / "items".
   */
  noun?: { singular: string; plural: string };
  /**
   * Optional class names — let pages tweak sticky offsets if needed.
   */
  className?: string;
}

/**
 * Sticky "N new since you opened this page" pill rendered above the filter
 * bar of a list card. Clicking the body scrolls the page up and clears the
 * count; the explicit Dismiss button clears without scrolling — useful when
 * the agent is mid-scroll and wants the highlight gone but doesn't want to
 * jump back to the top.
 *
 * Two side-by-side buttons (avoids nested-interactive HTML invariants).
 */
export function NewSincePill({
  count,
  onAcknowledge,
  noun = { singular: 'item', plural: 'items' },
  className,
}: NewSincePillProps) {
  if (count <= 0) return null;

  const label = `${count} new ${count === 1 ? noun.singular : noun.plural} since you opened this page`;

  const handlePrimary = () => {
    // Scroll the whole window — the list card itself isn't an internal
    // scroll container in this layout, so window scroll is what brings
    // the freshly arrived rows into view.
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    onAcknowledge();
  };

  return (
    <div
      className={cn(
        'sticky top-0 z-10 flex w-full items-stretch border-b border-info/30 bg-info/10 text-sm font-medium text-info animate-in fade-in slide-in-from-top-1 duration-200',
        className,
      )}
    >
      <button
        type="button"
        onClick={handlePrimary}
        className="flex flex-1 items-center gap-2 px-5 py-2.5 text-left transition-colors hover:bg-info/15 focus:bg-info/15 focus:outline-none"
      >
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-info/20">
          <ArrowUp className="h-3.5 w-3.5" />
        </span>
        <span className="truncate">{label}</span>
      </button>
      <button
        type="button"
        onClick={onAcknowledge}
        aria-label="Dismiss new-items banner"
        className="flex shrink-0 items-center gap-1 px-4 py-2.5 text-xs uppercase tracking-wide text-info/80 transition-colors hover:bg-info/15 hover:text-info focus:bg-info/15 focus:text-info focus:outline-none"
      >
        <X className="h-3 w-3" />
        Dismiss
      </button>
    </div>
  );
}
